import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, Eye, Unlock, Play, CheckCircle2, Repeat, Scissors, ChevronsUpDown } from "lucide-react";
import { getAnalytics, getAnalyticsDimensions, getPromoFragments } from "./adminApi";

const pct = (r) => `${Math.round((r || 0) * 100)}%`;
const pct1 = (r) => `${((r || 0) * 100).toFixed(1)}%`;
const mmss = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${(s % 60).toFixed(1).padStart(4, "0")}`;
};

const STAGES = [
  { key: "visits", label: "Visits", icon: Eye, unit: "Unique visitors" },
  { key: "unlocks", label: "Unlocks", icon: Unlock, unit: "Unique fans" },
  { key: "listens", label: "Listens", icon: Play, unit: "Unique fans" },
  { key: "complete_listens", label: "Complete", icon: CheckCircle2, unit: "Unique fans" },
  { key: "returning_fans", label: "Return", icon: Repeat, unit: "Unique fans" },
];

export default function Analytics() {
  const { slug } = useParams();
  const [dims, setDims] = useState({ platforms: [], triggers: [], content_pieces: [] });
  const [data, setData] = useState(null);
  const [fragments, setFragments] = useState(null);
  const [sort, setSort] = useState({ key: "unlock_conversion", dir: "desc" });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ date_from: "", date_to: "", platform: "", trigger: "", content_piece: "", broadcast_id: "", language: "", attr: "original" });
  const [range, setRange] = useState("all");

  useEffect(() => { getAnalyticsDimensions(slug).then(setDims).catch(() => {}); }, [slug]);

  const applyRange = (key) => {
    setRange(key);
    if (key === "custom" || key === "all") { if (key === "all") setF((p) => ({ ...p, date_from: "", date_to: "" })); return; }
    const today = new Date();
    const iso = (d) => {
      // local calendar date (YYYY-MM-DD), not UTC, so presets align to the admin's day
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    let from = "";
    if (key === "today") from = iso(today);
    else if (key === "7d") { const d = new Date(today); d.setDate(d.getDate() - 6); from = iso(d); }
    else if (key === "30d") { const d = new Date(today); d.setDate(d.getDate() - 29); from = iso(d); }
    setF((p) => ({ ...p, date_from: from, date_to: iso(today) }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const [a, pf] = await Promise.all([getAnalytics(slug, params), getPromoFragments(slug, params)]);
      setData(a);
      setFragments(pf);
    } catch (e) { toast.error("Could not load analytics"); }
    finally { setLoading(false); }
  }, [slug, f]);
  useEffect(() => { load(); }, [load]);

  const trigLabel = (id) => (dims.triggers.find((t) => t.id === id)?.label) || id;

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const sortedRows = (() => {
    if (!fragments) return [];
    const rows = [...fragments.rows];
    const k = sort.key, dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let av = a[k], bv = b[k];
      if (av == null) return 1;            // nulls (no clip) always last
      if (bv == null) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return rows;
  })();

  const FRAG_COLS = [
    ["content_piece", "Content Piece"], ["platform", "Platform"], ["trigger", "Trigger"],
    ["promo_start", "Promo Start"], ["promo_end", "Promo End"], ["promo_duration", "Duration"],
    ["visits", "Visits"], ["unlocks", "Unlocks"], ["unlock_conversion", "Unlock CR"],
    ["listens", "Listens"], ["complete_listens", "Complete"], ["completion_rate", "Compl. Rate"],
    ["returning_fans", "Returning"],
  ];

  return (
    <div data-testid="analytics-screen" className="space-y-8">
      <div>
        <h2 className="font-display text-3xl font-bold tracking-tight">Funnel Analytics</h2>
        <p className="text-[#8a8a8a] text-sm mt-1">Visit → Unlock → Listen → Complete → Return. Know what content actually converts fans.</p>
      </div>

      {/* quick date range */}
      <div className="flex flex-wrap items-center gap-2" data-testid="quick-range">
        {[["all", "All time"], ["today", "Today"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"], ["custom", "Custom"]].map(([k, label]) => (
          <button key={k} data-testid={`range-${k}`} onClick={() => applyRange(k)}
            className={`px-4 py-1.5 rounded-full text-xs font-mono-x tracking-wide uppercase transition-colors border ${
              range === k ? "bg-[#FF2A2A] border-[#FF2A2A] text-white" : "border-white/15 text-[#8a8a8a] hover:text-white hover:border-white/40"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* filters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">From</label>
          <input data-testid="filter-date-from" type="date" value={f.date_from} onChange={(e) => { setRange("custom"); setF({ ...f, date_from: e.target.value }); }}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">To</label>
          <input data-testid="filter-date-to" type="date" value={f.date_to} onChange={(e) => { setRange("custom"); setF({ ...f, date_to: e.target.value }); }}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Platform</label>
          <select data-testid="filter-platform" value={f.platform} onChange={(e) => setF({ ...f, platform: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All</option>
            {dims.platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Trigger</label>
          <select data-testid="filter-trigger" value={f.trigger} onChange={(e) => setF({ ...f, trigger: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All</option>
            {dims.triggers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Content piece</label>
          <select data-testid="filter-content" value={f.content_piece} onChange={(e) => setF({ ...f, content_piece: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All</option>
            {dims.content_pieces.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Broadcast</label>
          <select data-testid="filter-broadcast" value={f.broadcast_id} onChange={(e) => setF({ ...f, broadcast_id: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All</option>
            {(dims.broadcasts || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Language</label>
          <select data-testid="filter-language" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All</option>
            {(dims.languages || []).map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Attribution</label>
          <select data-testid="filter-attr" value={f.attr} onChange={(e) => setF({ ...f, attr: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="original">Original acquisition</option>
            <option value="current">Current session</option>
          </select>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center gap-2 text-[#737373]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* funnel */}
          <div className="flex items-center gap-3 mb-2 flex-wrap" data-testid="unit-legend">
            <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">Units:</span>
            <span className="font-mono-x text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-[#FF2A2A44] text-[#FF8a8a]">Unique visitors (anon)</span>
            <span className="font-mono-x text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-[#55E6B344] text-[#55E6B3]">Unique fans</span>
            <span className="font-mono-x text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-[#5B6CFF44] text-[#8a95ff]">Event counts</span>
            <span className="font-mono-x text-[9px] text-[#555] normal-case">— units differ, so compare within the same type only.</span>
          </div>
          <div className="flex flex-wrap items-stretch gap-2">
            {STAGES.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2">
                <div data-testid={`funnel-${s.key}`} className="rounded-2xl border border-white/10 bg-[#0c0c0f] px-5 py-4 min-w-[120px]">
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className="w-3.5 h-3.5 text-[#FF2A2A]" />
                    <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">{s.label}</span>
                  </div>
                  <p className="font-display text-3xl font-bold">{data.funnel[s.key]}</p>
                  <p className="font-mono-x text-[8px] tracking-wider uppercase text-[#555] mt-1">{s.unit}</p>
                </div>
                {i < STAGES.length - 1 && <ArrowRight className="w-4 h-4 text-[#444]" />}
              </div>
            ))}
          </div>
          {/* smart-link (event-count) metrics — different unit from the unique-user funnel above */}
          <div className="flex flex-wrap items-stretch gap-2" data-testid="smart-metrics">
            <div className="rounded-2xl border border-[#55E6B333] bg-[#55E6B30a] px-5 py-4 min-w-[150px]">
              <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Real Smart Landings</span>
              <p className="font-display text-3xl font-bold" style={{ color: "#55E6B3" }}>{data.funnel.smart_landings || 0}</p>
              <p className="font-mono-x text-[8px] tracking-wider uppercase text-[#555] mt-1">Unique · since telemetry deploy</p>
            </div>
            <div className="rounded-2xl border border-[#5B6CFF33] bg-[#5B6CFF0a] px-5 py-4 min-w-[150px]">
              <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Smart Preview Starts</span>
              <p className="font-display text-3xl font-bold">{data.funnel.smart_preview_starts || 0}</p>
              <p className="font-mono-x text-[8px] tracking-wider uppercase text-[#555] mt-1">Event counts</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0c0c0f] px-5 py-4 min-w-[150px]">
              <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Preview Starts</span>
              <p className="font-display text-3xl font-bold">{data.funnel.preview_starts || 0}</p>
              <p className="font-mono-x text-[8px] tracking-wider uppercase text-[#555] mt-1">Unique visitors</p>
            </div>
          </div>

          {/* Preview micro-funnel — pinpoint WHERE the Visit→Preview drop happens:
              real abandonment vs failed/blocked play vs media error (esp. in-app webviews). */}
          <div className="rounded-2xl border p-5 space-y-3" style={{ borderColor: "#5B6CFF33", background: "#5B6CFF08" }} data-testid="preview-micro-funnel">
            <div>
              <p className="font-mono-x text-[11px] tracking-widest uppercase text-white">Preview Micro-Funnel</p>
              <p className="font-mono-x text-[10px] text-[#737373] mt-1">Landing → Player Visible → Media Ready → Play Tap → Play Started. A big Tap→Started gap = blocked/failed playback (in-app webview / autoplay), NOT abandonment.</p>
              <p className="font-mono-x text-[10px] mt-1" style={{ color: "#55E6B3" }} data-testid="telemetry-note">
                {data.funnel.telemetry_start
                  ? `Preview telemetry available from ${new Date(data.funnel.telemetry_start).toLocaleString()} — Visitors counts only from this point (pre-telemetry visitors excluded).`
                  : "No preview telemetry yet — counts appear as live traffic arrives after the deploy."}
              </p>
            </div>
            <div className="flex flex-wrap items-stretch gap-2">
              {[
                ["Visitors", data.funnel.preview_window_visitors, "Unique · since telemetry", "#737373"],
                ["Player Visible", data.funnel.preview_visible, "Unique", "#737373"],
                ["Media Ready", data.funnel.preview_media_ready, "Unique", "#737373"],
                ["Play Tap", data.funnel.preview_play_taps, "Unique · intent", "#5B6CFF"],
                ["Play Started", data.funnel.preview_play_started, "Unique · success", "#55E6B3"],
                ["Preview Complete", data.funnel.preview_completes, "Unique", "#55E6B3"],
              ].map(([label, val, unit, color], i, arr) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="rounded-xl border border-white/10 bg-[#0c0c0f] px-4 py-3 min-w-[120px]">
                    <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">{label}</span>
                    <p className="font-display text-2xl font-bold" style={{ color }}>{val ?? 0}</p>
                    <p className="font-mono-x text-[8px] tracking-wider uppercase text-[#555] mt-0.5">{unit}</p>
                  </div>
                  {i < arr.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-[#444]" />}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <div className="rounded-lg px-3 py-2" style={{ background: "#FF6B6B14", border: "1px solid #FF6B6B44" }}>
                <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#FF6B6B]">Play Errors (blocked/failed)</span>
                <p className="font-display text-xl font-bold text-[#FF6B6B]">{data.funnel.preview_play_errors || 0}</p>
              </div>
              <div className="rounded-lg px-3 py-2" style={{ background: "#FFB02014", border: "1px solid #FFB02044" }}>
                <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#FFB020]">Media Errors (file/URL)</span>
                <p className="font-display text-xl font-bold text-[#FFB020]">{data.funnel.preview_media_errors || 0}</p>
              </div>
              <div className="rounded-lg px-3 py-2 border border-white/10">
                <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">Tap → Started</span>
                <p className="font-display text-xl font-bold text-white">{pct((data.funnel.preview_play_taps ? (data.funnel.preview_play_started || 0) / data.funnel.preview_play_taps : 0))}</p>
              </div>
            </div>
          </div>

          {/* Preview micro-funnel breakdown by Browser/WebView and by Traffic Source */}
          {[["By Browser / WebView", data.funnel.preview_by_webview, "preview-breakdown-webview"],
            ["By Traffic Source", data.funnel.preview_by_source, "preview-breakdown-source"]].map(([title, rows, tid]) => (
            <div key={tid} className="rounded-2xl border border-white/10 p-5 overflow-x-auto" data-testid={tid}>
              <p className="font-mono-x text-[11px] tracking-widest uppercase text-white mb-1">Preview Micro-Funnel · {title}</p>
              <p className="font-mono-x text-[10px] text-[#737373] mb-3">Compare where each channel drops. Visible→Tap = intent/UX. Tap→Started = playback success (low here = blocked/failed play, e.g. in-app webview).</p>
              <table className="w-full text-left border-collapse min-w-[720px]">
                <thead>
                  <tr className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">
                    {["Channel","Landing","Smart Land.","Visible","Ready","Tap","Started","Complete","Vis→Tap","Tap→Started","Play Err","Media Err"].map((h) => (
                      <th key={h} className="py-2 pr-3 font-normal whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(rows || []).length === 0 ? (
                    <tr><td colSpan={12} className="py-4 font-mono-x text-[11px] text-[#555]">No preview telemetry yet — data appears after redeploy + live traffic.</td></tr>
                  ) : (rows || []).map((r) => (
                    <tr key={r.key} className="border-t border-white/5 text-sm">
                      <td className="py-2 pr-3 font-mono-x text-[11px] text-white whitespace-nowrap">{r.key}</td>
                      <td className="py-2 pr-3 text-white/70">{r.landing}</td>
                      <td className="py-2 pr-3 text-white/70">{r.smart_landing}</td>
                      <td className="py-2 pr-3 text-white/70">{r.visible}</td>
                      <td className="py-2 pr-3 text-white/70">{r.ready}</td>
                      <td className="py-2 pr-3 font-bold" style={{ color: "#5B6CFF" }}>{r.tap}</td>
                      <td className="py-2 pr-3 font-bold" style={{ color: "#55E6B3" }}>{r.started}</td>
                      <td className="py-2 pr-3 text-white/70">{r.completed}</td>
                      <td className="py-2 pr-3 text-white/70">{pct(r.visible_to_tap)}</td>
                      <td className="py-2 pr-3 font-bold" style={{ color: r.tap_to_started < 0.5 && r.tap ? "#FF6B6B" : "#55E6B3" }}>{pct(r.tap_to_started)}</td>
                      <td className="py-2 pr-3 text-[#FF6B6B]">{r.play_errors}</td>
                      <td className="py-2 pr-3 text-[#FFB020]">{r.media_errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Technical Diagnostics — NOT a human-conversion metric; kept out of the main funnel */}
          <div className="rounded-xl border border-white/10 px-4 py-3 flex items-center gap-4 flex-wrap" data-testid="technical-diagnostics">
            <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#555]">Technical Diagnostics</span>
            <div className="flex items-center gap-2">
              <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Smart Resolve Events</span>
              <span className="font-display text-lg font-bold text-[#8a8a8a]">{data.funnel.smart_trigger_clicks || 0}</span>
            </div>
            <span className="font-mono-x text-[8px] tracking-wider uppercase text-[#FFB020]">Server hits · incl. refresh/prefetch/scrape · NOT real clicks or humans</span>
          </div>

          {/* rates */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/10 p-5">
              <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Unlock Conversion</p>
              <p data-testid="rate-unlock" className="font-display text-3xl font-bold text-[#FF2A2A] mt-1">{pct(data.rates.unlock_conversion)}</p>
              <p className="font-mono-x text-[10px] text-[#555] mt-1">Unlocks / Visits</p>
            </div>
            <div className="rounded-2xl border border-white/10 p-5">
              <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Completion Rate</p>
              <p data-testid="rate-completion" className="font-display text-3xl font-bold mt-1">{pct(data.rates.completion_rate)}</p>
              <p className="font-mono-x text-[10px] text-[#555] mt-1">Completes / Listens</p>
            </div>
            <div className="rounded-2xl border p-5" style={{ borderColor: "#5B6CFF55", background: "#5B6CFF0d" }}>
              <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Trigger → Preview Rate</p>
              <p data-testid="rate-trigger-preview" className="font-display text-3xl font-bold mt-1" style={{ color: "#5B6CFF" }}>{pct(data.rates.trigger_to_preview)}</p>
              <p className="font-mono-x text-[10px] text-[#555] mt-1">Smart preview starts ({data.funnel.smart_preview_starts || 0}) / Smart clicks ({data.funnel.smart_trigger_clicks || 0})</p>
              <p className="font-mono-x text-[9px] text-[#FFB020] mt-1" data-testid="attribution-label">Profile-link = campaign attribution (not exact-post)</p>
            </div>
            <div className="rounded-2xl border p-5" style={{ borderColor: "#5B6CFF55", background: "#5B6CFF0d" }}>
              <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Preview → Unlock CR</p>
              <p data-testid="rate-preview-unlock" className="font-display text-3xl font-bold mt-1" style={{ color: "#5B6CFF" }}>{pct(data.rates.preview_to_unlock)}</p>
              <p className="font-mono-x text-[10px] text-[#555] mt-1">Unlocks / Preview starts ({data.funnel.preview_starts})</p>
            </div>
            <div className="rounded-2xl border p-5" style={{ borderColor: "#55E6B355", background: "#55E6B30d" }}>
              <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Unlock → First Listen CR</p>
              <p data-testid="rate-unlock-first-listen" className="font-display text-3xl font-bold mt-1" style={{ color: "#55E6B3" }}>{pct(data.rates.unlock_to_first_listen)}</p>
              <p className="font-mono-x text-[10px] text-[#555] mt-1">First listens / Unlocks ({data.funnel.first_listens})</p>
            </div>
            <div className="rounded-2xl border border-white/10 p-5">
              <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Returning Fans</p>
              <p data-testid="stat-returning" className="font-display text-3xl font-bold mt-1">{data.funnel.returning_fans}</p>
              <p className="font-mono-x text-[10px] text-[#555] mt-1">Active on 2+ days</p>
            </div>
          </div>

          {/* by platform */}
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight mb-3">By acquisition platform</h3>
            <p className="font-mono-x text-[10px] text-[#737373] -mt-2 mb-3 normal-case">Cohort of fans who unlocked in the selected range (Unlocks/Listens/Completes/Returning = unique fans).</p>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm" data-testid="by-platform-table">
                <thead>
                  <tr className="text-left font-mono-x text-[10px] tracking-widest uppercase text-[#737373] border-b border-white/10">
                    <th className="px-4 py-3">Platform</th><th className="px-4 py-3">Unlocks</th>
                    <th className="px-4 py-3">Listens</th><th className="px-4 py-3">Completes</th><th className="px-4 py-3">Returning</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_platform.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-6 text-[#737373]">No fans yet.</td></tr>
                  ) : data.by_platform.map((b) => (
                    <tr key={b.platform} className="border-b border-white/5">
                      <td className="px-4 py-3 font-medium">{b.platform}</td>
                      <td className="px-4 py-3">{b.unlocks}</td>
                      <td className="px-4 py-3">{b.listens}</td>
                      <td className="px-4 py-3">{b.completes}</td>
                      <td className="px-4 py-3">{b.returning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* by language */}
          <div data-testid="by-language">
            <h3 className="font-display text-lg font-bold tracking-tight mb-3">By language</h3>
            <p className="font-mono-x text-[10px] text-[#737373] -mt-2 mb-3 normal-case">Visits = unique visitors · Unlocks/Listens/Complete = unique fans in the range's unlock cohort.</p>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm" data-testid="by-language-table">
                <thead>
                  <tr className="text-left font-mono-x text-[10px] tracking-widest uppercase text-[#737373] border-b border-white/10">
                    <th className="px-4 py-3">Language</th><th className="px-4 py-3">Visits</th>
                    <th className="px-4 py-3">Unlocks</th><th className="px-4 py-3">Unlock CR</th>
                    <th className="px-4 py-3">Listens</th><th className="px-4 py-3">Complete</th>
                    <th className="px-4 py-3">Compl. Rate</th><th className="px-4 py-3">Returning</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_language || []).length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-6 text-[#737373]">No language data yet.</td></tr>
                  ) : data.by_language.map((b) => (
                    <tr key={b.language} className="border-b border-white/5" data-testid={`lang-row-${b.language}`}>
                      <td className="px-4 py-3 font-medium uppercase">{b.language}</td>
                      <td className="px-4 py-3">{b.visits}</td>
                      <td className="px-4 py-3">{b.unlocks}</td>
                      <td className="px-4 py-3 text-[#FF2A2A] font-semibold">{pct1(b.unlock_conversion)}</td>
                      <td className="px-4 py-3">{b.listens}</td>
                      <td className="px-4 py-3">{b.complete_listens}</td>
                      <td className="px-4 py-3">{pct1(b.completion_rate)}</td>
                      <td className="px-4 py-3">{b.returning_fans}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* PROMO FRAGMENT PERFORMANCE */}
          <div data-testid="promo-fragment-performance">
            <div className="flex items-center gap-2 mb-1">
              <Scissors className="w-4 h-4 text-[#FF2A2A]" />
              <h3 className="font-display text-lg font-bold tracking-tight">Promo Fragment Performance</h3>
            </div>
            <p className="text-[#8a8a8a] text-sm mb-3">Which fragment of the master converts best. Timecodes come from each content piece's Promo Clip. Click a column to sort.</p>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm whitespace-nowrap" data-testid="promo-fragment-table">
                <thead>
                  <tr className="text-left font-mono-x text-[10px] tracking-widest uppercase text-[#737373] border-b border-white/10">
                    {FRAG_COLS.map(([k, label]) => (
                      <th key={k} data-testid={`frag-col-${k}`} onClick={() => toggleSort(k)}
                        className="px-4 py-3 cursor-pointer hover:text-white select-none">
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {sort.key === k ? <span className="text-[#FF2A2A]">{sort.dir === "desc" ? "↓" : "↑"}</span> : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr><td colSpan={FRAG_COLS.length} className="px-4 py-6 text-[#737373]">No content pieces yet.</td></tr>
                  ) : sortedRows.map((r) => (
                    <tr key={r.content_piece} data-testid={`frag-row-${r.content_piece}`}
                      className={`border-b border-white/5 ${r.has_clip ? "" : "opacity-70"}`}>
                      <td className="px-4 py-3 font-medium">{r.content_piece}</td>
                      <td className="px-4 py-3 text-[#cfcfcf]">{r.platform}</td>
                      <td className="px-4 py-3 text-[#cfcfcf]">{r.trigger}</td>
                      <td className="px-4 py-3 font-mono-x text-xs">{mmss(r.promo_start)}</td>
                      <td className="px-4 py-3 font-mono-x text-xs">{mmss(r.promo_end)}</td>
                      <td className="px-4 py-3">{r.promo_duration != null ? `${r.promo_duration}s` : "—"}</td>
                      <td className="px-4 py-3">{r.visits}</td>
                      <td className="px-4 py-3">{r.unlocks}</td>
                      <td className="px-4 py-3 text-[#FF2A2A] font-semibold">{pct1(r.unlock_conversion)}</td>
                      <td className="px-4 py-3">{r.listens}</td>
                      <td className="px-4 py-3">{r.complete_listens}</td>
                      <td className="px-4 py-3">{pct1(r.completion_rate)}</td>
                      <td className="px-4 py-3">{r.returning_fans}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
